import React, { useState, useEffect, useMemo } from 'react';
import { Container } from 'reactstrap';
import { NavMenu } from './NavMenu.js';
import UserContext from '../providers/UserContext.js';
import { BillingClient, CurrentUserClient } from '../web-api-client.ts';
import Loader from './Loader.js';
import { useLocation } from 'react-router-dom';

type BillingStatus = 'pending_setup' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'cancel_scheduled';

type BillingStatusResponse = {
  status: BillingStatus;
  amountCents: number;
  currency: string;
  currentPeriodEnd?: string | null; // ISO string (server sends DateTime)
  trialEnd?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  cancelAt?: string | null;
  canceledAt?: string | null;
};

const WHITELIST_PREFIXES = [
  '/activate',
  '/billing',
  '/billing/complete',
  '/billing/success',
  '/get-started',
  '/dvi/view',
  '/login'
];

function isWhitelisted(pathname: string) {
  return WHITELIST_PREFIXES.some(p => pathname.startsWith(p));
}

function isAccessAllowed(b: BillingStatusResponse | null): boolean {
  if (!b) return true; // be lenient on unknown (or change to false if you prefer strict)
  if (b.status === 'active' || b.status === 'trialing') return true;

  if (b.status === 'cancel_scheduled') {
    const end = b.currentPeriodEnd ? new Date(b.currentPeriodEnd).getTime() : 0;
    return Date.now() < end;
  }

  // pending_setup, past_due, canceled → block
  return false;
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Skip gating on whitelisted routes
      if (isWhitelisted(pathname)) {
        setLoading(false);
        return;
      }

      try {
        const user = await new CurrentUserClient().getCurrentUser();
        if (cancelled) return;

        setCurrentUser(user);

        // Pull billing status for the user's dealer
        const billing: BillingStatusResponse =
          await new BillingClient().billing_GetBillingStatus(user.dealerId);

        if (cancelled) return;

        if (!isAccessAllowed(billing)) {
          // Decide where to send them based on status
          if (billing.status === 'pending_setup') {
            window.location.href = '/activate';
          } else {
            // past_due, canceled, or cancel_scheduled after period end
            window.location.href = '/billing/complete';
          }
          return;
        }
      } catch {
        // On error, you can choose to allow or block; we'll allow to avoid false locks.
        // To be strict, redirect to /billing/complete here.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [pathname]);

  const contextValue = useMemo(() => (currentUser), [currentUser]);

  if (loading) return <Loader />;

  return (
    <UserContext.Provider value={contextValue}>
      <NavMenu />
      <Container tag="main">{children}</Container>
    </UserContext.Provider>
  );
}
